import { useEffect, useState } from "react";
import { KeyRound, Webhook, Plus, Trash2, Copy, Loader2, Send, Code, CheckCircle2, Smartphone, AlertTriangle, Users, History, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { IntegrationInfoPanel } from "@/components/IntegrationInfo";
import { Sms2ckPanel } from "@/components/Sms2ckPanel";
import { SetupChecklist } from "@/components/SetupChecklist";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EVENTS = ["message.received", "message.sent", "campaign.sent"];

export default function Integrazioni() {
  const [keys, setKeys] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [newKey, setNewKey] = useState(null);

  const load = async () => {
    const [k, w] = await Promise.all([api.get("/api-keys"), api.get("/webhooks")]);
    setKeys(k.data);
    setHooks(w.data);
  };
  useEffect(() => { load(); }, []);

  const createKey = async () => {
    const { data } = await api.post("/api-keys", { name: "Chiave app esterna" });
    setNewKey(data);
    load();
  };
  const revokeKey = async (id) => { await api.delete(`/api-keys/${id}`); toast.success("Chiave revocata"); load(); };
  const delHook = async (id) => { await api.delete(`/webhooks/${id}`); toast.success("Webhook eliminato"); load(); };
  const testHook = async (id) => { await api.post(`/webhooks/${id}/test`); toast.success("Evento di test inviato"); };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copiato"); };

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-100">Integrazioni</h1>
        <p className="text-slate-400 text-sm mt-1">Collega le tue app: invia messaggi, avvia campagne e ricevi eventi via webhook</p>
      </div>

      {/* Meta WhatsApp connection */}
      <SetupChecklist />
      <MetaPanel copy={copy} />
      <IntegrationInfoPanel />
      <SmsPanel />
      <Sms2ckPanel />

      <UsersPanel />
      <ActivityPanel />

      {/* API Keys */}
      <section className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[#10B981]" />
            <h2 className="text-lg font-semibold text-slate-100">Chiavi API</h2>
          </div>
          <Button onClick={createKey} data-testid="create-apikey-button" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            <Plus className="h-4 w-4 mr-2" /> Genera chiave
          </Button>
        </div>
        {keys.length === 0 ? (
          <p className="text-sm text-slate-500">Nessuna chiave. Genera una chiave per far inviare messaggi alle tue app.</p>
        ) : (
          <div className="space-y-2" data-testid="apikeys-list">
            {keys.map((k) => (
              <div key={k.id} className="flex items-center justify-between bg-[#0A0E17] border border-[#1F2937] rounded-xl px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm text-slate-200">{k.name} <span className="font-mono2 text-xs text-slate-500">{k.prefix}…</span></p>
                  <p className="text-xs text-slate-500">
                    {k.revoked ? <span className="text-[#EF4444]">Revocata</span> : "Attiva"} · ultimo uso: {k.last_used ? new Date(k.last_used).toLocaleString("it-IT") : "mai"}
                  </p>
                </div>
                {!k.revoked && (
                  <button onClick={() => revokeKey(k.id)} data-testid={`revoke-apikey-${k.id}`} className="text-slate-500 hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Webhooks */}
      <section className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Webhook className="h-5 w-5 text-[#3B82F6]" />
            <h2 className="text-lg font-semibold text-slate-100">Webhook in uscita</h2>
          </div>
          <WebhookDialog onDone={load} />
        </div>
        {hooks.length === 0 ? (
          <p className="text-sm text-slate-500">Nessun webhook. Aggiungi un URL per ricevere eventi nelle tue app.</p>
        ) : (
          <div className="space-y-2" data-testid="webhooks-list">
            {hooks.map((h) => (
              <div key={h.id} className="flex items-center justify-between bg-[#0A0E17] border border-[#1F2937] rounded-xl px-4 py-3 gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-200 font-mono2 truncate">{h.url}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {h.events.map((e) => <Badge key={e} className="bg-[#1E293B] text-slate-400 border-0 text-[10px]">{e}</Badge>)}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Secret: <span className="font-mono2">{h.secret}</span></p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => testHook(h.id)} data-testid={`test-webhook-${h.id}`} className="text-slate-500 hover:text-[#10B981]" title="Invia evento di test"><Send className="h-4 w-4" /></button>
                  <button onClick={() => delHook(h.id)} data-testid={`delete-webhook-${h.id}`} className="text-slate-500 hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Docs */}
      <section className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-5 space-y-3" data-testid="api-docs">
        <div className="flex items-center gap-2">
          <Code className="h-5 w-5 text-[#8B5CF6]" />
          <h2 className="text-lg font-semibold text-slate-100">Collega la tua app (es. fidelity)</h2>
        </div>
        <p className="text-sm text-slate-400">Tutte le chiamate usano la chiave nell'header <span className="font-mono2 text-slate-300">X-API-Key</span>. Flusso tipico: <b className="text-slate-300">1)</b> sincronizza il cliente → <b className="text-slate-300">2)</b> invia un template approvato o avvia un'automazione.</p>
        <p className="text-xs text-slate-500 font-medium">1. Crea/aggiorna il cliente (anagrafica, consenso, punti e campi personalizzati):</p>
        <CodeBlock onCopy={copy} text={`curl -X POST "${BACKEND}/api/public/contacts" \\
  -H "X-API-Key: LA_TUA_CHIAVE" -H "Content-Type: application/json" \\
  -d '{"phone":"+39333...","name":"Mario","surname":"Rossi","card_code":"FID-00123","store":"Salerno",
       "consent":true,"tags":["fidelity"],"custom_fields":{"punti":"1250","saldo":"12,50"}}'`} />
        <p className="text-xs text-slate-500 font-medium">2a. Invia un template approvato (funziona sempre, anche fuori dalla finestra 24h). Le variabili del template vengono riempite con i dati del contatto o con <span className="font-mono2">variables</span>:</p>
        <CodeBlock onCopy={copy} text={`curl -X POST "${BACKEND}/api/public/messages" \\
  -H "X-API-Key: LA_TUA_CHIAVE" -H "Content-Type: application/json" \\
  -d '{"phone":"+39333...","template_id":"ID_O_NOME_META_TEMPLATE","variables":{"punti":"1250"}}'`} />
        <p className="text-xs text-slate-500 font-medium">2b. Testo libero (solo se il cliente ti ha scritto nelle ultime 24h; supporta {"{{nome}}"}, {"{{card_code}}"}, campi personalizzati):</p>
        <CodeBlock onCopy={copy} text={`curl -X POST "${BACKEND}/api/public/messages" \\
  -H "X-API-Key: LA_TUA_CHIAVE" -H "Content-Type: application/json" \\
  -d '{"phone":"+39333...","text":"Ciao {{nome}}, hai {{punti}} punti sulla carta {{card_code}}!"}'`} />
        <p className="text-xs text-slate-500 font-medium">2c. Avvia un'automazione (percorso completo con azioni, tag e sequenze):</p>
        <CodeBlock onCopy={copy} text={`curl -X POST "${BACKEND}/api/public/automations/ID_AUTOMAZIONE/trigger" \\
  -H "X-API-Key: LA_TUA_CHIAVE" -H "Content-Type: application/json" \\
  -d '{"phone":"+39333...","variables":{"punti":"1250"}}'`} />
        <p className="text-xs text-slate-500">Elenchi: <span className="font-mono2">GET {BACKEND}/api/public/automations</span> · <span className="font-mono2">GET {BACKEND}/api/public/campaigns</span> · avvio campagna: <span className="font-mono2">POST {BACKEND}/api/public/campaigns/ID/send</span>. Limite 60 richieste/minuto per chiave. In alternativa puoi importare i clienti con un CSV dalla Rubrica o far partire automazioni dal trigger "Integrazione (Webhook)".</p>
        <p className="text-xs text-slate-500">Vuoi ricevere gli eventi nella tua app (risposte dei clienti, consegne, clic)? Registra un <b>Webhook in uscita</b> qui sopra.</p>
      </section>

      {/* new key reveal */}
      <Dialog open={!!newKey} onOpenChange={(o) => { if (!o) setNewKey(null); }}>
        <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100">
          <DialogHeader><DialogTitle className="font-display">Chiave API creata</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#F59E0B]">Copiala adesso: per sicurezza non sarà più mostrata.</p>
            <div className="flex items-center gap-2 bg-[#0A0E17] border border-[#1F2937] rounded-xl px-3 py-2">
              <code className="text-xs text-[#10B981] font-mono2 break-all flex-1" data-testid="new-apikey-value">{newKey?.key}</code>
              <button onClick={() => copy(newKey?.key)} className="text-slate-400 hover:text-slate-100"><Copy className="h-4 w-4" /></button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setNewKey(null)} className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">Ho copiato</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "admin" });
  const [busy, setBusy] = useState(false);
  const [pwdFor, setPwdFor] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const load = () => api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);
  const genPwd = () => Array.from(crypto.getRandomValues(new Uint8Array(12))).map((n) => "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789"[n % 56]).join("");
  const create = async () => {
    if (!form.email || !form.password) { toast.error("Email e password sono obbligatorie"); return; }
    setBusy(true);
    try { await api.post("/auth/register", form); toast.success(`Utente ${form.email} creato`); setForm({ email: "", name: "", password: "", role: "admin" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const toggle = async (u) => {
    try { await api.put(`/auth/users/${u.id}/status`, { disabled: !u.disabled }); toast.success(u.disabled ? "Account riattivato" : "Account disattivato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const savePwd = async () => {
    try { await api.put(`/auth/users/${pwdFor.id}/password`, { password: newPwd }); toast.success("Password aggiornata"); setPwdFor(null); setNewPwd(""); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'account ${u.email}?`)) return;
    try { await api.delete(`/auth/users/${u.id}`); toast.success("Utente eliminato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <section className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-5 space-y-4" data-testid="users-panel">
      <div className="flex items-center gap-2">
        <Users className="h-5 w-5 text-[#8B5CF6]" />
        <h2 className="text-lg font-semibold text-slate-100">Gestione utenti</h2>
        <span className="text-xs text-slate-500">Account che accedono all'area riservata (nessun accesso all'ambiente di sviluppo)</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
        <Input placeholder="email@esempio.it" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" className="bg-[#0A0E17] border-[#1F2937] text-slate-100" />
        <Input placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" className="bg-[#0A0E17] border-[#1F2937] text-slate-100" />
        <div className="flex gap-1">
          <Input placeholder="Password (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" className="bg-[#0A0E17] border-[#1F2937] text-slate-100 font-mono2" />
          <Button type="button" variant="outline" onClick={() => setForm({ ...form, password: genPwd() })} data-testid="user-genpwd-button" title="Genera password" className="bg-[#1E293B] border-[#1F2937] text-slate-200 px-2"><RefreshCw className="h-4 w-4" /></Button>
        </div>
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role-select"
                className="h-10 rounded-md bg-[#0A0E17] border border-[#1F2937] text-slate-100 text-sm px-3">
          <option value="admin">Admin</option><option value="member">Membro</option>
        </select>
        <Button onClick={create} disabled={busy} data-testid="user-create-button" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Crea utente</>}
        </Button>
      </div>
      <div className="rounded-xl border border-[#1F2937] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0E17] text-slate-500 text-xs"><tr><th className="text-left px-4 py-2 font-medium">Utente</th><th className="text-left px-4 py-2 font-medium">Ruolo</th><th className="text-left px-4 py-2 font-medium">Stato</th><th className="text-left px-4 py-2 font-medium hidden md:table-cell">Ultimo accesso</th><th className="px-4 py-2" /></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#1F2937]" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-2"><p className="text-slate-200">{u.name || "—"}</p><p className="text-xs text-slate-500">{u.email}</p></td>
                <td className="px-4 py-2"><Badge className={`border-0 text-[10px] ${u.role === "admin" ? "bg-[#8B5CF6]/15 text-[#8B5CF6]" : "bg-slate-500/15 text-slate-300"}`}>{u.role}</Badge></td>
                <td className="px-4 py-2">{u.disabled ? <Badge className="bg-[#EF4444]/15 text-[#EF4444] border-0 text-[10px]">disattivato</Badge> : <Badge className="bg-[#10B981]/15 text-[#10B981] border-0 text-[10px]">attivo</Badge>}</td>
                <td className="px-4 py-2 text-xs text-slate-500 hidden md:table-cell">{u.last_login_at ? new Date(u.last_login_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "mai"}</td>
                <td className="px-4 py-2">
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => { setPwdFor(u); setNewPwd(genPwd()); }} data-testid={`user-pwd-${u.email}`} title="Cambia password" className="text-slate-400 hover:text-[#38BDF8]"><KeyRound className="h-4 w-4" /></button>
                    <Switch checked={!u.disabled} onCheckedChange={() => toggle(u)} data-testid={`user-toggle-${u.email}`} />
                    <button type="button" onClick={() => remove(u)} data-testid={`user-delete-${u.email}`} className="text-slate-500 hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!pwdFor} onOpenChange={(o) => { if (!o) setPwdFor(null); }}>
        <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100">
          <DialogHeader><DialogTitle className="font-display">Nuova password per {pwdFor?.email}</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Input value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="user-newpwd-input" className="bg-[#0A0E17] border-[#1F2937] text-slate-100 font-mono2" />
            <Button type="button" variant="outline" onClick={() => setNewPwd(genPwd())} className="bg-[#1E293B] border-[#1F2937] text-slate-200 px-2"><RefreshCw className="h-4 w-4" /></Button>
          </div>
          <p className="text-xs text-slate-500">Comunica la nuova password all'utente: non viene inviata via email.</p>
          <DialogFooter><Button onClick={savePwd} data-testid="user-savepwd-button" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">Salva password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function ActivityPanel() {
  const [data, setData] = useState({ items: [], users: [], workspaces: [] });
  const [who, setWho] = useState("all");
  const [ws, setWs] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [exporting, setExporting] = useState(false);
  const params = () => ({ ...(who !== "all" ? { user_email: who } : {}), ...(ws !== "all" ? { workspace: ws } : {}), ...(start ? { start } : {}), ...(end ? { end } : {}) });
  useEffect(() => {
    api.get("/activity", { params: params() }).then((r) => setData(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [who, ws, start, end]);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/activity", { params: { ...params(), format: "csv" }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `log_attivita_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Log attività esportato");
    } catch (e) { toast.error("Esportazione non riuscita"); } finally { setExporting(false); }
  };
  return (
    <section className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-5 space-y-3" data-testid="activity-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-[#F59E0B]" />
          <h2 className="text-lg font-semibold text-slate-100">Log attività</h2>
          <span className="text-xs text-slate-500">Chi ha fatto cosa (invii, contatti, automazioni…)</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(data.workspaces || []).length > 1 && (
            <select value={ws} onChange={(e) => setWs(e.target.value)} data-testid="activity-workspace-filter"
                    className="h-9 rounded-md bg-[#0A0E17] border border-[#1F2937] text-slate-100 text-sm px-3">
              <option value="all">Tutti i canali</option>
              {data.workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          )}
          <select value={who} onChange={(e) => setWho(e.target.value)} data-testid="activity-user-filter"
                  className="h-9 rounded-md bg-[#0A0E17] border border-[#1F2937] text-slate-100 text-sm px-3">
            <option value="all">Tutti gli utenti</option>
            {data.users.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="activity-start-date" className="h-9 w-[150px] bg-[#0A0E17] border-[#1F2937] text-slate-100 text-sm" />
          <span className="text-xs text-slate-500">→</span>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="activity-end-date" className="h-9 w-[150px] bg-[#0A0E17] border-[#1F2937] text-slate-100 text-sm" />
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={exporting} data-testid="activity-export-button"
                  className="h-9 bg-[#1E293B] border-[#38BDF8]/20 text-[#38BDF8] hover:bg-[#334155]">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1" /> Esporta CSV</>}
          </Button>
        </div>
      </div>
      <div className="rounded-xl border border-[#1F2937] max-h-[360px] overflow-y-auto">
        {data.items.length === 0 ? <p className="text-xs text-slate-500 p-4" data-testid="activity-empty">Nessuna attività registrata.</p> : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#0A0E17] text-slate-500"><tr><th className="text-left px-4 py-2 font-medium">Quando</th><th className="text-left px-4 py-2 font-medium">Canale</th><th className="text-left px-4 py-2 font-medium">Utente</th><th className="text-left px-4 py-2 font-medium">Azione</th><th className="text-left px-4 py-2 font-medium hidden lg:table-cell">Dettaglio</th></tr></thead>
            <tbody>
              {data.items.map((it) => (
                <tr key={it.id} className="border-t border-[#1F2937]" data-testid="activity-row">
                  <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{new Date(it.ts).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</td>
                  <td className="px-4 py-2 text-slate-300 whitespace-nowrap" data-testid="activity-row-workspace">{it.workspace_name || it.workspace}</td>
                  <td className="px-4 py-2 text-slate-300">{it.user_name || it.user_email}<span className="block text-[10px] text-slate-500">{it.user_email}</span></td>
                  <td className="px-4 py-2 text-slate-200">{it.label}</td>
                  <td className="px-4 py-2 text-slate-500 font-mono2 hidden lg:table-cell">{it.method} {it.path.replace("/api", "")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function SmsPanel() {
  const [st, setSt] = useState(null);
  const [form, setForm] = useState({ sms_sender: "", sms_unit_cost: 0.055, sms_simulate: false, sms_credit_threshold: 500 });
  const [testPhone, setTestPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [hooks, setHooks] = useState(null);
  const loadHooks = async () => { try { const { data } = await api.get("/sms/webhooks"); setHooks(data); } catch { setHooks({ error: "Stato webhook non disponibile" }); } };
  useEffect(() => { loadHooks(); }, []);
  const load = async () => {
    try {
      const [s, a] = await Promise.all([api.get("/sms/status"), api.get("/app-settings")]);
      setSt(s.data);
      setForm({ sms_sender: a.data.sms_sender || "", sms_unit_cost: a.data.sms_unit_cost ?? 0.055, sms_simulate: !!a.data.sms_simulate, sms_credit_threshold: a.data.sms_credit_threshold ?? 500 });
    } catch { setSt({ configured: false, error: "Stato non disponibile" }); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/app-settings", { sms_sender: form.sms_sender, sms_unit_cost: parseFloat(form.sms_unit_cost || "0"), sms_simulate: form.sms_simulate, sms_credit_threshold: parseInt(form.sms_credit_threshold || "0", 10) }); toast.success("Impostazioni SMS salvate"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Salvataggio non riuscito"); } finally { setBusy(false); }
  };
  const test = async (simulate) => {
    if (!testPhone.trim()) { toast.error("Inserisci un numero"); return; }
    setBusy(true);
    try { const { data } = await api.post("/sms/test", { phone: testPhone, simulate }); toast.success(`${simulate ? "Simulazione OK" : "SMS inviato"}: ${data.sent} inviato, crediti ${data.credits}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test non riuscito"); } finally { setBusy(false); }
  };
  return (
    <section className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-5 space-y-4" data-testid="sms-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2"><Smartphone className="h-5 w-5 text-[#3B82F6]" /><h2 className="text-lg font-semibold text-slate-100">Canale SMS (SMSFactor)</h2></div>
        {st && <span className={`text-xs px-2 py-1 rounded-full ${st.configured && !st.error ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#EF4444]/15 text-[#EF4444]"}`} data-testid="sms-status-badge">{st.configured ? (st.error ? "Errore" : "Connesso") : "Non configurato"}</span>}
      </div>
      {!st ? <Loader2 className="h-5 w-5 animate-spin text-[#10B981]" /> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Info label="Crediti SMSFactor" value={st.unlimited ? `${st.credits ?? "—"} (illimitato)` : st.credits ?? "—"} />
            <Info label="SMS inviati nel mese" value={String(st.sent_month ?? 0)} />
            <Info label="Fallback SMS nel mese" value={String(st.fallback_month ?? 0)} />
            <Info label="Modalità" value={form.sms_simulate ? "Simulazione (nessun credito)" : "Invio reale"} />
          </div>
          {st.error && <p className="text-xs text-[#EF4444]" data-testid="sms-error">{st.error}</p>}
          <div className="grid sm:grid-cols-4 gap-3">
            <div className="space-y-1"><Label className="text-xs text-slate-400">Mittente (alias approvato, max 11 caratteri)</Label>
              <Input value={form.sms_sender} onChange={(e) => setForm({ ...form, sms_sender: e.target.value })} placeholder="es. ViviClub" className="bg-[#0A0E17] border-[#1F2937] h-9" data-testid="sms-sender-input" /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-400">Costo per SMS (€) per report e budget</Label>
              <Input type="number" step="0.001" value={form.sms_unit_cost} onChange={(e) => setForm({ ...form, sms_unit_cost: e.target.value })} className="bg-[#0A0E17] border-[#1F2937] h-9" data-testid="sms-unit-cost-input" /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-400">Avviso email sotto (crediti)</Label>
              <Input type="number" min="0" step="1" value={form.sms_credit_threshold} onChange={(e) => setForm({ ...form, sms_credit_threshold: e.target.value })} className="bg-[#0A0E17] border-[#1F2937] h-9" data-testid="sms-credit-threshold-input" />
              <p className="text-[11px] text-slate-500" data-testid="sms-credit-alert-status">{st.credit_alerted ? `Avviso inviato${st.credit_alerted_at ? ` il ${new Date(st.credit_alerted_at).toLocaleDateString("it-IT")}` : ""}: crediti sotto soglia` : "Controllo ogni 6 ore · 0 = disattivato"}</p></div>
            <div className="flex items-end justify-between gap-3 pb-1">
              <div><Label className="text-xs text-slate-400">Simulazione</Label><p className="text-[11px] text-slate-500">Prova senza consumare crediti</p></div>
              <Switch checked={form.sms_simulate} onCheckedChange={(v) => setForm({ ...form, sms_simulate: v })} data-testid="sms-simulate-switch" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={save} disabled={busy} className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold h-9" data-testid="sms-save-button">Salva impostazioni SMS</Button>
            <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+39…" className="bg-[#0A0E17] border-[#1F2937] h-9 w-40" data-testid="sms-test-phone" />
            <Button variant="outline" onClick={() => test(true)} disabled={busy} className="bg-[#1E293B] border-[#1F2937] text-slate-200 h-9" data-testid="sms-test-simulate-button">Prova (simulazione)</Button>
            <Button variant="outline" onClick={() => test(false)} disabled={busy} className="bg-[#1E293B] border-[#3B82F6]/50 text-[#3B82F6] h-9" data-testid="sms-test-real-button">Invia SMS reale di prova</Button>
          </div>
          <div className="rounded-xl border border-[#1F2937] bg-[#0B1120] p-3 space-y-2" data-testid="sms-webhooks-box">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <p className="text-xs font-medium text-slate-200">Esiti di consegna e risposte (webhook SMSFactor)</p>
                <p className="text-[11px] text-slate-500">
                  {hooks === null ? "Lettura…" : hooks.error ? hooks.error
                    : hooks.missing?.length === 0 ? "DLR, MO e STOP registrati verso questa app: consegnati/falliti nel report campagna e STOP via SMS automatico."
                      : `Da registrare: ${hooks.missing?.join(", ")} — gli esiti SMS arrivano solo dopo la registrazione.`}
                </p>
                {hooks?.last && (hooks.last.dlr || hooks.last.stop) && <p className="text-[11px] text-slate-500">Ultimo esito: {hooks.last.dlr ? new Date(hooks.last.dlr).toLocaleString("it-IT") : "—"} · ultimo STOP: {hooks.last.stop ? new Date(hooks.last.stop).toLocaleString("it-IT") : "—"}</p>}
              </div>
              <Button variant="outline" size="sm" disabled={busy || hooks?.missing?.length === 0} data-testid="sms-register-webhooks-button"
                      onClick={async () => { setBusy(true); try { const { data } = await api.post("/sms/webhooks/register"); toast.success(data.created.length ? `Registrati: ${data.created.join(", ")}${data.replaced?.length ? ` (sostituiti i vecchi: ${data.replaced.join(", ")})` : ""}` : "Webhook già registrati"); if (data.errors?.length) toast.error(data.errors.join(" · ")); loadHooks(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Registrazione non riuscita"); } finally { setBusy(false); } }}
                      className="bg-[#1E293B] border-[#1F2937] text-slate-200 h-8">
                {hooks?.missing?.length === 0 ? <><CheckCircle2 className="h-3.5 w-3.5 mr-1 text-[#10B981]" /> Registrati</> : "Registra webhook esiti"}
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-slate-500">Il canale SMS si usa in Nuova campagna (canale «SMS» o «Fallback SMS» su WhatsApp), in Chat (pulsante SMS) e nelle Automazioni (azione «Invia un SMS»). Il mittente alfanumerico va registrato e approvato su SMSFactor (AGCOM): senza alias approvato lascia vuoto.</p>
        </>
      )}
    </section>
  );
}


function MetaPanel({ copy }) {
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [to, setTo] = useState("+39");
  const [tpl, setTpl] = useState("hello_world");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [daily, setDaily] = useState("1000");
  const [savingDaily, setSavingDaily] = useState(false);
  const [qLevel, setQLevel] = useState("any");
  const [autoTier, setAutoTier] = useState(true);
  const [hotReply, setHotReply] = useState("");

  const load = async () => {
    try {
      const s = await api.get("/whatsapp/status");
      setStatus(s.data);
      if (s.data.configured) {
        const t = await api.get("/whatsapp/live-templates");
        setTemplates(t.data);
        const approved = t.data.find((x) => x.status === "APPROVED");
        if (approved) setTpl(`${approved.name}|${approved.language}`);
      }
      const st = await api.get("/app-settings");
      setDaily(String(st.data.daily_send_limit ?? 1000));
      setQLevel(st.data.quality_alert_level || "any");
      setAutoTier(st.data.auto_tier_limit ?? true);
      setHotReply(st.data.hot_click_reply || "");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel caricamento stato Meta");
    }
  };
  useEffect(() => { load(); }, []);

  const saveDaily = async () => {
    setSavingDaily(true);
    try {
      await api.put("/app-settings", { daily_send_limit: parseInt(daily || "0", 10), quality_alert_level: qLevel, auto_tier_limit: autoTier, hot_click_reply: hotReply });
      toast.success("Impostazioni salvate");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Salvataggio non riuscito");
    } finally { setSavingDaily(false); }
  };

  const sendTest = async () => {
    if (!to.trim() || to.trim().length < 8) { toast.error("Inserisci un numero valido con prefisso"); return; }
    setSending(true);
    setResult(null);
    const [name, language] = tpl.includes("|") ? tpl.split("|") : [tpl, "en_US"];
    try {
      const { data } = await api.post("/whatsapp/test-send", { to: to.trim(), template_name: name, language });
      setResult({ ok: true, wamid: data.wamid });
      toast.success("Messaggio inviato! Meta lo ha accettato.");
    } catch (err) {
      setResult({ ok: false, error: formatApiErrorDetail(err.response?.data?.detail) });
      toast.error("Invio non riuscito");
    } finally { setSending(false); }
  };

  const configured = status?.configured;
  const phone = status?.phone;

  return (
    <section className="bg-gradient-to-br from-[#10B981]/10 to-[#111827]/80 border border-[#10B981]/30 rounded-2xl p-5 space-y-4" data-testid="meta-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Smartphone className="h-5 w-5 text-[#10B981]" />
          <h2 className="text-lg font-semibold text-slate-100">Collegamento WhatsApp (Meta)</h2>
        </div>
        {configured ? (
          <Badge className="bg-[#10B981]/20 text-[#10B981] border-0" data-testid="meta-connected-badge">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Collegato
          </Badge>
        ) : (
          <Badge className="bg-[#EF4444]/20 text-[#EF4444] border-0">Non configurato</Badge>
        )}
      </div>

      {phone && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Info label="Numero" value={phone.display_phone_number} />
          <div className="bg-[#0A0E17] rounded-lg p-2.5" data-testid="display-name-info">
            <p className="text-[11px] text-slate-500 mb-0.5">Nome visualizzato</p>
            <p className="text-sm font-medium text-slate-200 truncate">{phone.verified_name || "—"}</p>
            {phone.name_status && <p className={`text-[10px] ${phone.name_status === "APPROVED" ? "text-[#10B981]" : "text-[#F59E0B]"}`}>Stato: {phone.name_status}</p>}
            {phone.new_display_name && phone.new_display_name !== phone.verified_name && (
              <p className="text-[10px] text-[#F59E0B]" data-testid="new-display-name">Nuovo nome «{phone.new_display_name}»: {phone.new_name_status || "in attesa"}</p>
            )}
          </div>
          <div className="bg-[#0A0E17] rounded-lg p-2.5" data-testid="quality-info">
            <p className="text-[11px] text-slate-500 mb-0.5">Qualità</p>
            <p className={`text-sm font-medium ${phone.quality_rating === "GREEN" ? "text-[#10B981]" : phone.quality_rating === "YELLOW" ? "text-[#F59E0B]" : phone.quality_rating === "RED" ? "text-[#EF4444]" : "text-slate-200"}`}>
              {phone.quality_rating || "—"}
            </p>
          </div>
          <Info label="Modalità campagne" value={status.mode === "live" ? "LIVE" : "Demo"} />
        </div>
      )}

      {phone && (
        <div className="bg-[#0A0E17] border border-[#1F2937] rounded-lg px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="tier-info">
          <span className="text-[11px] uppercase tracking-wide text-slate-500">Scaglione Meta</span>
          <Badge className="bg-[#10B981]/15 text-[#10B981] border-0 font-mono2" data-testid="tier-badge">
            {status.messaging_limit || "—"}
          </Badge>
          <span className="text-xs text-slate-500">determina quanti clienti unici puoi contattare al giorno</span>
        </div>
      )}

      {status?.error && (
        <div className="flex items-start gap-2 text-sm text-[#FCA5A5] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg px-3 py-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{status.error}</span>
        </div>
      )}

      {/* Webhook config */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-400">Configura questo webhook su Meta (App → WhatsApp → Configurazione), campo <b>messages</b>:</Label>
        <Row label="URL Callback" value={status?.webhook_url} copy={copy} />
        <Row label="Token di verifica" value={status?.verify_token} copy={copy} />
        {status?.webhook && <WebhookHealth h={status.webhook} />}
      </div>

      {/* Test send */}
      <div className="bg-[#0A0E17] border border-[#1F2937] rounded-xl p-4 space-y-3">
        <p className="text-sm font-medium text-slate-200">Invia un messaggio di prova reale</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input data-testid="meta-test-to" value={to} onChange={(e) => setTo(e.target.value)}
                 placeholder="+39333..." className="bg-[#111827] border-[#1F2937] sm:max-w-[200px]" />
          <select data-testid="meta-test-template" value={tpl} onChange={(e) => setTpl(e.target.value)}
                  className="bg-[#111827] border border-[#1F2937] rounded-md px-3 py-2 text-sm text-slate-200 flex-1">
            {templates.length === 0 && <option value="hello_world">hello_world (en_US)</option>}
            {templates.filter((t) => t.status === "APPROVED").map((t) => (
              <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>
                {t.name} ({t.language}) · {t.category}
              </option>
            ))}
          </select>
          <Button onClick={sendTest} disabled={sending || !configured} data-testid="meta-test-send"
                  className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Invia prova</>}
          </Button>
        </div>
        {result?.ok && <p className="text-xs text-[#10B981] break-all" data-testid="meta-test-result">✓ Accettato da Meta · ID: {result.wamid}</p>}
        {result && !result.ok && <p className="text-xs text-[#FCA5A5]" data-testid="meta-test-result">✗ {result.error}</p>}
        <p className="text-xs text-slate-500">Nota: i nuovi contatti possono ricevere solo <b>template approvati</b>. Il testo libero funziona solo entro 24h da un messaggio del cliente.</p>
      </div>

      {/* Daily sending limit (Meta tier) */}
      <div className="bg-[#0A0E17] border border-[#1F2937] rounded-xl p-4 space-y-3" data-testid="daily-limit-panel">
        <div>
          <p className="text-sm font-medium text-slate-200">Limite invii reali al giorno (scaglione Meta)</p>
          <p className="text-xs text-slate-500 mt-0.5">Le campagne reali che superano questo limite inviano fino al tetto e riprendono automaticamente il giorno dopo. Imposta <b>0</b> per illimitato.</p>
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="quality-alert-row">
            <span className="text-xs text-slate-400">Allarme qualità numero via email:</span>
            <select value={qLevel} onChange={(e) => setQLevel(e.target.value)} data-testid="quality-alert-select"
                    className="bg-[#111827] border border-[#1F2937] rounded-md text-xs text-slate-100 px-2 py-1">
              <option value="any">A ogni calo (GREEN → YELLOW/RED)</option>
              <option value="red">Solo quando diventa RED (critico)</option>
            </select>
          </div>
          <div className="mt-3 flex items-center justify-between gap-3 bg-[#111827] border border-[#1F2937] rounded-lg px-3 py-2" data-testid="auto-tier-row">
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-200">Aumenta il limite automaticamente</p>
              <p className="text-[11px] text-slate-500">Quando Meta promuove il numero a uno scaglione superiore, il limite giornaliero viene alzato da solo (non viene mai abbassato).</p>
            </div>
            <Switch checked={autoTier} onCheckedChange={setAutoTier} data-testid="auto-tier-switch" />
          </div>
          <div className="mt-3 bg-[#111827] border border-[#1F2937] rounded-lg px-3 py-2 space-y-1.5" data-testid="hot-click-reply-row">
            <p className="text-xs font-medium text-slate-200">Risposta rapida "clic caldo"</p>
            <p className="text-[11px] text-slate-500">Messaggio inviato dal pulsante "Rispondi subito" in Chat quando un cliente clicca il link con finestra 24h aperta. Puoi usare {"{{nome}}"}.</p>
            <textarea value={hotReply} onChange={(e) => setHotReply(e.target.value)} rows={3} data-testid="hot-click-reply-input"
                      className="w-full bg-[#0A0E17] border border-[#1F2937] rounded-md text-xs text-slate-100 px-2 py-1.5 resize-none" />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <Input data-testid="daily-limit-input" type="number" min="0" value={daily} onChange={(e) => setDaily(e.target.value)}
                 className="bg-[#111827] border-[#1F2937] sm:max-w-[200px]" placeholder="1000" />
          <div className="flex flex-wrap gap-1.5">
            {[250, 1000, 10000, 100000, 0].map((v) => (
              <button key={v} type="button" data-testid={`daily-preset-${v}`} onClick={() => setDaily(String(v))}
                      className={`px-2.5 py-1 rounded-md text-xs font-mono2 border ${String(v) === String(daily) ? "bg-[#10B981]/20 border-[#10B981]/40 text-[#10B981]" : "bg-[#111827] border-[#1F2937] text-slate-400 hover:text-slate-200"}`}>
                {v === 0 ? "illimitato" : v.toLocaleString("it-IT")}
              </button>
            ))}
          </div>
          <Button onClick={saveDaily} disabled={savingDaily} data-testid="daily-limit-save"
                  className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold sm:ml-auto">
            {savingDaily ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva limite"}
          </Button>
        </div>
      </div>
    </section>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-[#0A0E17] border border-[#1F2937] rounded-lg px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-slate-200 truncate">{value || "—"}</p>
    </div>
  );
}

function WebhookHealth({ h }) {
  const [repairing, setRepairing] = useState(false);
  const [sub, setSub] = useState(h.subscription);
  useEffect(() => setSub(h.subscription), [h.subscription]);
  const repair = async () => {
    setRepairing(true);
    try {
      const { data } = await api.post("/whatsapp/webhook/repair");
      setSub(data.subscription);
      toast.success("Webhook configurato su Meta: campi messages + stato template attivi");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Riparazione non riuscita"); }
    finally { setRepairing(false); }
  };
  const subProblem = sub?.configured && !sub.error && (sub.callback_ok === false || (sub.missing_fields || []).length > 0 || sub.active === false);
  const kinds = { message: "messaggio in arrivo", status: "stato consegna/lettura", template: "stato template" };
  const kindTxt = h.last_event_kind ? h.last_event_kind.split(",").map((k) => kinds[k] || k).join(" + ") : null;
  const last = h.last_event_at ? new Date(h.last_event_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "mai ricevuto";
  const ago = h.hours_since_last === null || h.hours_since_last === undefined ? "" : h.hours_since_last < 1 ? "meno di un'ora fa" : `${Math.round(h.hours_since_last)}h fa`;
  const color = h.status === "ok" ? "#10B981" : h.status === "stale" ? "#EF4444" : "#94A3B8";
  return (
    <div className={`rounded-lg border px-3 py-2 space-y-1 ${h.stale ? "border-[#EF4444]/50 bg-[#EF4444]/10" : "border-[#1F2937] bg-[#111827]"}`} data-testid="webhook-health">
      <div className="flex items-center gap-2 text-sm">
        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: color, boxShadow: `0 0 8px ${color}` }} data-testid="webhook-health-dot" />
        <span className="text-slate-200 font-medium">Webhook Meta: {h.status === "ok" ? "attivo" : h.status === "stale" ? "fermo" : "nessun evento ricevuto"}</span>
        <span className="text-xs text-slate-500 ml-auto" data-testid="webhook-last-event">Ultimo evento: {last}{ago ? ` (${ago})` : ""}{kindTxt ? ` · ${kindTxt}` : ""}</span>
      </div>
      {h.stale && (
        <p className="text-xs text-[#FCA5A5] flex items-start gap-1.5" data-testid="webhook-stale-warning">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{h.pending_after_last_event} {h.pending_after_last_event === 1 ? "messaggio inviato" : "messaggi inviati"} dopo l'ultimo evento ricevuto {h.pending_after_last_event === 1 ? "è" : "sono"} ancora senza stato consegnato/letto da oltre {h.grace_hours} ora{h.newest_pending_sent_at ? ` (ultimo invio: ${new Date(h.newest_pending_sent_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })})` : ""}. Meta non sta recapitando gli eventi: verifica che la Callback URL qui sopra sia quella configurata e che il campo <b>messages</b> sia sottoscritto, oppure usa «Riallinea webhook su Meta».</span>
        </p>
      )}
      {!h.stale && h.pending_status_messages > 0 && <p className="text-[11px] text-slate-500">{h.pending_status_messages} messaggi delle ultime 48h in attesa di stato da Meta (normale se il destinatario è offline o ha bloccato il numero).</p>}
      {sub && (
        <div className="pt-1 border-t border-[#1F2937]/60 space-y-1" data-testid="webhook-subscription">
          {!sub.configured ? (
            <p className="text-[11px] text-slate-500">Per controllare e riparare la configurazione webhook direttamente da qui imposta <b>WHATSAPP_APP_SECRET</b> (Meta → Impostazioni → Di base → Segreto dell'app).</p>
          ) : sub.error ? (
            <p className="text-[11px] text-[#FCA5A5]">Lettura configurazione Meta non riuscita: {sub.error}</p>
          ) : (
            <>
              <p className="text-[11px] text-slate-400" data-testid="webhook-subscription-fields">
                Su Meta: callback {sub.callback_ok ? <span className="text-[#10B981]">corretta</span> : <span className="text-[#FCA5A5]">diversa ({sub.callback_url || "nessuna"})</span>} · campi iscritti: {(sub.fields || []).length ? sub.fields.join(", ") : "nessuno"}
                {(sub.missing_fields || []).length > 0 && <span className="text-[#FCA5A5]"> · mancano: <b>{sub.missing_fields.join(", ")}</b></span>}
              </p>
              {subProblem && <p className="text-[11px] text-[#FCA5A5]">Senza il campo <b>messages</b> non arrivano né i messaggi dei clienti né gli stati di consegna.</p>}
            </>
          )}
          {sub.configured && !sub.error && (
            <Button size="sm" variant="outline" onClick={repair} disabled={repairing} data-testid="webhook-repair-button"
                    className={`h-7 text-xs ${subProblem ? "border-[#EF4444]/60 text-[#FCA5A5] hover:bg-[#EF4444]/10" : "border-[#1F2937] text-slate-300"}`}>
              {repairing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}{subProblem ? "Ripara webhook su Meta" : "Riallinea webhook su Meta"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, copy }) {
  return (
    <div className="flex items-center gap-2 bg-[#0A0E17] border border-[#1F2937] rounded-lg px-3 py-2">
      <span className="text-xs text-slate-500 w-28 shrink-0">{label}</span>
      <code className="text-xs text-slate-300 font-mono2 break-all flex-1">{value || "—"}</code>
      <button onClick={() => copy(value)} className="text-slate-500 hover:text-slate-100 shrink-0"><Copy className="h-4 w-4" /></button>
    </div>
  );
}

function CodeBlock({ text, onCopy }) {
  return (
    <div className="relative bg-[#0A0E17] border border-[#1F2937] rounded-xl p-3">
      <pre className="text-xs text-slate-300 font-mono2 overflow-x-auto whitespace-pre-wrap">{text}</pre>
      <button onClick={() => onCopy(text)} className="absolute top-2 right-2 text-slate-500 hover:text-slate-100"><Copy className="h-4 w-4" /></button>
    </div>
  );
}

function WebhookDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState([...EVENTS]);
  const [saving, setSaving] = useState(false);

  const toggleEvent = (e) => setEvents((ev) => (ev.includes(e) ? ev.filter((x) => x !== e) : [...ev, e]));

  const save = async () => {
    if (!url.trim() || !/^https?:\/\//.test(url.trim())) { toast.error("Inserisci un URL valido (http/https)"); return; }
    setSaving(true);
    try {
      await api.post("/webhooks", { url: url.trim(), events, enabled: true });
      toast.success("Webhook aggiunto");
      setOpen(false); setUrl(""); setEvents([...EVENTS]);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="add-webhook-button" className="bg-[#1E293B] border-[#3B82F6]/20 text-[#3B82F6] hover:bg-[#334155]">
          <Plus className="h-4 w-4 mr-2" /> Aggiungi webhook
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Nuovo webhook</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>URL endpoint della tua app</Label>
            <Input data-testid="webhook-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
                   className="bg-[#0A0E17] border-[#1F2937]" placeholder="https://miaapp.it/webhook" /></div>
          <div className="space-y-2"><Label>Eventi</Label>
            <div className="space-y-2">
              {EVENTS.map((e) => (
                <label key={e} className="flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" checked={events.includes(e)} onChange={() => toggleEvent(e)} data-testid={`webhook-event-${e}`}
                         className="accent-[#10B981]" />
                  <span className="font-mono2">{e}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="save-webhook-button" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aggiungi"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
